import { resolveTimeWindow } from "@/lib/date-range";
import type { RequestQueryParams } from "@/lib/http-client";
import type { ToolExecutionsFilters } from "../search";

const trimmed = (value: string): string | undefined => {
  const next = value.trim();
  return next === "" ? undefined : next;
};

/**
 * A multi-value filter goes on the wire as one comma-separated param — the
 * engine reads it that way, and a repeated param would silently keep only the
 * first value. A listed value matches if the execution matches any of them.
 */
export function toCsvFilter(values: readonly string[]): string | undefined {
  const cleaned = values
    .map((value) => value.trim())
    .filter((value) => value !== "");
  return cleaned.length === 0 ? undefined : cleaned.join(",");
}

export function toolExecutionsQueryParams(
  filters: ToolExecutionsFilters
): RequestQueryParams {
  const { toolName, toolkitNames, userId, outcome, failureReason } = filters;
  const toolkitFilter = toCsvFilter(toolkitNames);

  return {
    ...(trimmed(toolName) && { tool_name: toolName.trim() }),
    ...(toolkitFilter !== undefined && { toolkit_name: toolkitFilter }),
    ...(trimmed(userId) && { user_id: userId.trim() }),
    ...(outcome !== "all" && { outcome }),
    ...(trimmed(failureReason) && { failure_reason: failureReason.trim() }),
    ...resolveTimeWindow({
      dateRange: filters.dateRange,
      startTime: filters.startTime,
      endTime: filters.endTime,
    }),
  };
}

export function countActiveFilters(filters: ToolExecutionsFilters): number {
  const { toolName, toolkitNames, userId, outcome, failureReason, dateRange } =
    filters;
  const textFilters = [toolName, userId, failureReason].filter(
    (value) => trimmed(value) !== undefined
  ).length;
  // However many toolkits are selected, the caller narrowed one dimension.
  const toolkitFilter = toCsvFilter(toolkitNames) === undefined ? 0 : 1;
  const outcomeFilter = outcome === "all" ? 0 : 1;
  // The count drives whether a reset is offered, so what matters is that the
  // caller moved off the default — not whether the selection narrowed anything
  // yet. A custom range with neither bound filled sends no time param, but it
  // still reads as a range on the trigger and needs a way back.
  const timeFilter = dateRange === "all" ? 0 : 1;

  return textFilters + toolkitFilter + outcomeFilter + timeFilter;
}
